import { Checkbox } from "@kobalte/core/checkbox";
import { debounce } from "@solid-primitives/scheduled";
import { A } from "@solidjs/router";
import { timestampToDate, useAssetClient, useCallOptions, withBearerToken } from "@storytell/cp-client";
import { AssetLifecycleState } from "@storytell/cp-client/src/gen/assets/v3/asset_pb";
import { dispatchGlobalEvent } from "@storytell/events";
import type { Asset, AssetId, Concept } from "@storytell/types";
import { StButton } from "@storytell/ui/buttons";
import { StPopover, StPopoverTrigger } from "@storytell/ui/popover";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import {
  type ColumnDef,
  type Header,
  type SortingState,
  createSolidTable,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
} from "@tanstack/solid-table";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import {
  TbAlertTriangle,
  TbCaretDown,
  TbCaretUp,
  TbCircleCheckFilled,
  TbLoader2,
  TbMessage2,
  TbTag,
  TbTrash,
} from "solid-icons/tb";
import { TbCheck } from "solid-icons/tb";
import {
  For,
  Match,
  Show,
  Switch,
  batch,
  createEffect,
  createMemo,
  createSignal,
  on,
  onCleanup,
  onMount,
} from "solid-js";
import { twMerge } from "tailwind-merge";
import { TbStackPlus } from "~/components/knowledge/TbStackPlus";
import type { EntitySelection } from "~/components/knowledge/useEntitySelection";
import { AssetLabelsRow } from "~/components/labels/AssetLabelsRow";
import { BulkAssetLabelPicker } from "~/components/labels/BulkAssetLabelPicker";
import { lifecycleStateGrouped } from "~/components/utils/lifecycleStateMappers";
import { useWire } from "~/wire";
import { useAppUI } from "./AppUIContext";
dayjs.extend(localizedFormat);

export const NavigationAssetsPane = (props: {
  search: string;
  setSearch: (s: string) => void;
  buildFilterFunction: (item: Asset | Concept) => boolean;
  setLabelFilter: (labelId: string) => void;
  selection: EntitySelection;

  assetToHighlight: string;
  setAssetToHighlight: (s: string) => void;
  labelToFilterBy: string;
  setLabelToFilterBy: (s: string) => void;
  mount?: Node;
}) => {
  const wire = useWire();
  // createEffect(
  //   on(
  //     () => props.labelToFilterBy,
  //     (labelId) => {
  //       if (!labelId) return;
  //       props.filterBuilder.setFilter({
  //         operator: "and",
  //         filter: (asset) => filterAssetByLabel(asset, labelId),
  //         filterId: labelId,
  //       });
  //       props.setLabelToFilterBy("");
  //     },
  //   ),
  // );

  // Sorting based on initial scope to bring the in-scope assets to the top
  // but also prevent them from shifting around as the user adds/removes assets
  const assets = createMemo(() => wire.sync.assets.search(props.search).filter(props.buildFilterFunction));

  // Default sorting: by last updated, descending (newest first)
  const [sorting, setSorting] = createSignal<SortingState>([{ id: "Last Updated", desc: true }]);

  const ui = useAppUI();
  const [deleting, setDeleting] = createSignal<Record<string, boolean>>({});

  const onBulkDelete = async () => {
    if (!window.confirm("Are you sure you want to bulk delete these assets?")) return;

    const ids = Object.entries(props.selection.selected())
      .filter(([_, value]) => value)
      .map(([id]) => id);

    if (ids.length === 0) return;

    const assetClient = useAssetClient();
    setDeleting((prev) => ({ ...prev, ...props.selection.selected() }));

    try {
      const callOptions = useCallOptions(withBearerToken(wire.auth.token()));
      const result = await assetClient.deleteAssets({ assetIds: ids }, callOptions);

      if (result.isErr()) {
        console.error(result.error);
        throw result.error;
      }

      if (result.isOk()) {
        for (const asset of result.value.assets) {
          batch(() => {
            wire.sync.onAssetDeleted(asset.data.id as AssetId);
          });
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      const deleted = ids.reduce(
        (acc, id) => {
          acc[id] = false;
          return acc;
        },
        {} as Record<string, boolean>,
      );

      setDeleting((prev) => ({ ...prev, ...deleted }));
    }
  };

  const onSingleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this asset?")) return;

    const assetClient = useAssetClient();

    setDeleting((prev) => ({ ...prev, [id]: true }));
    try {
      const callOptions = useCallOptions(withBearerToken(wire.auth.token()));
      const result = await assetClient.deleteAsset(
        {
          assetId: id,
        },
        callOptions,
      );
      if (result.isErr()) {
        console.error(result.error);
        throw result.error;
      }

      if (result.isOk()) {
        const asset = result.value.asset;
        wire.sync.onAssetDeleted(asset.data.id as AssetId);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setDeleting((prev) => ({ ...prev, [id]: false }));
      props.selection.setSelected((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  // Table columns definition
  const columns: ColumnDef<Asset>[] = [
    {
      id: "bulk-select",
      header: () => (
        <Checkbox
          checked={props.selection.allSelected(assets())}
          indeterminate={props.selection.someSelected(assets()) && !props.selection.allSelected(assets())}
          aria-label={props.selection.allSelected(assets()) ? "Deselect all" : "Select all"}
          onChange={(value) => props.selection.toggleAll(value, assets())}
          class="flex items-center justify-center"
        >
          <Checkbox.Input />
          <Checkbox.Control
            data-some-selected={props.selection.someSelected(assets()) && !props.selection.allSelected(assets())}
            class="w-4 h-4 border border-primary-border dark:border-white/30 rounded bg-tile data-[some-selected=true]:bg-black/30 dark:data-[some-selected=true]:bg-white/30 data-[checked]:bg-brand data-[checked]:border-brand flex items-center justify-center transition-colors"
          >
            <Show when={props.selection.allSelected(assets()) || props.selection.someSelected(assets())}>
              <Checkbox.Indicator as={TbCheck} class="w-4 h-4 text-on-brand stroke-3" />
            </Show>
          </Checkbox.Control>
        </Checkbox>
      ),
      cell: (info) => {
        const id = () => info.row.original.id;
        const checked = () => !!props.selection.selected()[id()];
        return (
          <Checkbox
            checked={checked()}
            aria-label={checked() ? "Deselect asset" : "Select asset"}
            onChange={(value) => {
              props.selection.setSelected((prev) => ({ ...prev, [id()]: value }));
            }}
            class="flex items-center justify-center"
          >
            <Checkbox.Input />
            <Checkbox.Control class="w-4 h-4 border border-primary-border dark:border-white/30 rounded bg-tile data-[checked]:bg-brand data-[checked]:border-brand flex items-center justify-center transition-colors">
              <Show when={checked()}>
                <Checkbox.Indicator as={TbCheck} class="w-4 h-4 text-on-brand stroke-3" />
              </Show>
            </Checkbox.Control>
          </Checkbox>
        );
      },
      footer: (props) => props.column.id,
      enableSorting: false,
    },
    {
      header: "Name",
      accessorFn: (row) => row.fileName,
      cell: (info) => {
        let btn!: HTMLButtonElement;
        onMount(() => {
          const leave = debounce(() => {
            const parent = btn.parentElement;
            if (!parent) return;
            parent.style.removeProperty("max-width");
          }, 300);
          const enter = () => {
            const parent = btn.parentElement;
            if (!parent) return;
            leave.clear();
            const width = parent.getBoundingClientRect().width - 38;
            parent.style.setProperty("max-width", `${width}px`);
          };

          btn.addEventListener("mouseenter", enter);
          btn.addEventListener("mouseleave", leave);
          onCleanup(() => {
            btn.removeEventListener("mouseenter", enter);
            btn.removeEventListener("mouseleave", leave);
          });
        });
        return (
          <button
            ref={btn}
            onClick={() => {
              dispatchGlobalEvent("add-items-to-prompt-scope", { ids: [info.row.original.id] });
            }}
            type="button"
            class="group/btn relative text-left cursor-pointer font-medium text-primary-text max-w-full line-clamp-1 wrap-anywhere py-1 px-2 border border-transparent rounded-lg hover:border-primary-border transition-all"
          >
            <span class="absolute left-2 opacity-0 group-hocus/btn:opacity-100 transition-all duration-100">
              Chat with
            </span>
            <span class="group-hocus/btn:ml-17 transition-all duration-100 text-transparent bg-clip-text bg-primary-text group-hocus/btn:bg-gradient-to-r group-hocus/btn:from-violet-600 group-hocus/btn:to-blue-600 group-hocus/btn:dark:from-violet-400 group-hocus/btn:dark:to-blue-400">
              <span class="hidden group-hocus/btn:inline">@</span>
              {String(info.getValue())}
            </span>
          </button>
        );
      },
      footer: (props) => props.column.id,
      enableSorting: true,
    },
    {
      header: "Labels",
      cell: (info) => (
        <AssetLabelsRow
          assetId={info.row.original.id}
          mount={panelRef}
          setSearch={props.setSearch}
          addFilter={(label) => {
            // filterBuilder.setFilter({
            //   operator: "and",
            //   filter: (asset) => filterAssetByLabel(asset, label),
            //   filterId: label,
            // });
          }}
          selectedAssets={props.selection.selectedIds()}
        />
      ),
    },
    // TODO: @andi, add file size
    // {
    //   header: "File Size",
    //   accessorFn: (row) => row.fileSize,
    //   cell: (info) => <span class="whitespace-nowrap">{formatFileSize(info.getValue() as number | bigint)}</span>,
    //   footer: (props) => props.column.id,
    // },
    {
      header: "Last Updated",
      accessorFn: (row) => new Date(row.modifiedAt).getTime(),
      cell: (info) => {
        const date = () => {
          return dayjs(info.row.original.modifiedAt).format("LLL");
        };
        return <span class="text-xs">{date()}</span>;
      },
      footer: (props) => props.column.id,
      enableSorting: true,
    },
    {
      header: "Status",
      cell: (info) => {
        const value = () => lifecycleStateGrouped(info.row.original.lifecycleState);
        return (
          <Show
            when={value() === "insufficient credits"}
            fallback={
              <span class="flex items-center gap-1 capitalize text-xs">
                <Switch>
                  <Match when={value() === "uploading" || value() === "processing"}>
                    <TbLoader2 class="w-3 h-3 animate-spin group-hocus/btn:scale-hover group-active/btn:scale-active transition-transform text-muted-text" />
                  </Match>
                  <Match when={value() === "error"}>
                    <TbAlertTriangle class="w-3 h-3 text-red-700 dark:text-red-400 group-hocus/btn:scale-hover group-active/btn:scale-active transition-transform" />
                  </Match>
                  <Match when={value() === "ready"}>
                    <TbCircleCheckFilled class="w-3 h-3 group-hocus/btn:scale-hover group-active/btn:scale-active transition-transform text-muted-text" />
                  </Match>
                </Switch>
                {value()}
              </span>
            }
          >
            <StTooltip
              portalProps={{
                mount: props.mount,
              }}
              contentProps={{
                class: "z-50",
              }}
              placement="top"
              content={
                <>
                  <p>Storytell ran out of credits to process this asset.</p>
                  <p>
                    Please{" "}
                    <A onClick={() => ui.actions.closeSecondaryPane()} class="text-brand underline" href="/pricing">
                      top up your credits
                    </A>{" "}
                    and try again.
                  </p>
                </>
              }
            >
              <StTooltipTrigger class="flex items-center gap-1 capitalize text-xs">
                <TbAlertTriangle class="w-3 h-3 text-red-700 dark:text-red-400 group-hocus/btn:scale-hover group-active/btn:scale-active transition-transform" />
                Insufficient credits
              </StTooltipTrigger>
            </StTooltip>
          </Show>
        );
      },
      footer: (props) => props.column.id,
    },

    {
      header: " ",
      accessorFn: (row) => row.id,
      cell: (info) => {
        const id = () => String(info.getValue());
        return (
          <StButton
            class="bg-tile hover:bg-tile-hover"
            onClick={() => onSingleDelete(id())}
            icon={TbTrash}
            loading={deleting()[id()]}
          />
        );
      },
      footer: (props) => props.column.id,
    },
  ];

  // Create the table instance
  const table = createSolidTable({
    get data() {
      return assets();
    },
    columns,
    state: {
      get sorting() {
        return sorting();
      },
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: false,
  });

  // Keyboard handler for sorting
  function handleHeaderKeyDown(e: KeyboardEvent, header: Header<Asset, unknown>) {
    if (header.column.getCanSort() && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      const handler = header.column.getToggleSortingHandler();
      if (handler) handler(e);
    }
  }

  let panelRef!: HTMLDivElement;
  // let inputRef!: HTMLInputElement;
  // onMount(() => {
  //   inputRef.focus();
  // });

  const [bulkLabelPickerOpen, setBulkLabelPickerOpen] = createSignal(false);
  return (
    <div ref={panelRef} class="px-2">
      {/* <AssetsFilterView builder={filterBuilder} mount={panelRef} /> */}

      <Show when={props.selection.selectedIds().length}>
        {(len) => (
          <div class="flex items-center gap-3 sticky -top-2 z-10 bg-panel py-2">
            <StButton
              noColor
              class="shrink-0 bg-brand hover:bg-brand-hover text-on-brand"
              icon={TbMessage2}
              onClick={() => {
                dispatchGlobalEvent("add-items-to-prompt-scope", { ids: props.selection.selectedIds() });
              }}
              loading={Object.values(deleting()).some(Boolean)}
            >
              Chat with {len()} Asset{len() === 1 ? "" : "s"}
            </StButton>
            {/* <StButton icon={TbStackPlus} class="group/btn shrink-0 dark:bg-tile dark:hover:bg-tile-hover">
                Create View
              </StButton> */}
            <StPopover
              open={bulkLabelPickerOpen()}
              onOpenChange={setBulkLabelPickerOpen}
              contentProps={{ class: "z-50" }}
              portalProps={{
                mount: panelRef,
              }}
              placement="bottom-start"
              noArrow
              gutter={10}
              content={
                <BulkAssetLabelPicker
                  onLabelCreated={() => {
                    setBulkLabelPickerOpen(false);
                    dispatchGlobalEvent("refresh-sync", undefined);
                  }}
                  onClose={() => setBulkLabelPickerOpen(false)}
                  mount={panelRef}
                  selected={props.selection.selectedIds() as AssetId[]}
                />
              }
            >
              <StPopoverTrigger as={StButton} icon={TbTag} class="shrink-0 dark:bg-tile dark:hover:bg-tile-hover">
                Change Labels
              </StPopoverTrigger>
            </StPopover>
            <StButton
              noColor
              class="shrink-0"
              icon={TbTrash}
              onClick={onBulkDelete}
              loading={Object.values(deleting()).some(Boolean)}
            >
              Delete {len()} Asset{len() === 1 ? "" : "s"}
            </StButton>
          </div>
        )}
      </Show>

      <div
        class={twMerge(
          "rounded-lg border border-primary-border flex-auto overflow-auto max-h-[calc(100vh-24vh)]",
          props.selection.selectedIds().length > 0 && "max-h-[calc(100vh-30vh)]",
        )}
      >
        <div class="grid grid-cols-[40px_minmax(200px,1fr)_auto_auto_auto_auto] text-sm min-w-full">
          {/* Header */}
          <For each={table.getHeaderGroups()}>
            {(headerGroup) => (
              <For each={headerGroup.headers}>
                {(header) => (
                  <div class="text-xs px-2 py-2 font-medium text-left text-primary-text whitespace-nowrap sticky top-0 z-10 bg-canvas border-b border-primary-border">
                    <Show when={!header.isPlaceholder}>
                      <div
                        class={
                          header.column.getCanSort() ? "cursor-pointer select-none flex items-center gap-1" : undefined
                        }
                        tabIndex={header.column.getCanSort() ? 0 : undefined}
                        role={header.column.getCanSort() ? "button" : undefined}
                        aria-pressed={header.column.getIsSorted() ? "true" : "false"}
                        onClick={header.column.getToggleSortingHandler()}
                        onKeyDown={(e) => handleHeaderKeyDown(e as KeyboardEvent, header)}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        <Show when={header.column.getIsSorted() === "asc"}>
                          <TbCaretUp class="inline-block w-4 h-4 text-brand" />
                        </Show>
                        <Show when={header.column.getIsSorted() === "desc"}>
                          <TbCaretDown class="inline-block w-4 h-4 text-brand" />
                        </Show>
                      </div>
                    </Show>
                  </div>
                )}
              </For>
            )}
          </For>

          {/* Body */}
          <Show
            when={table.getRowModel().rows.length > 0}
            fallback={<div class="col-span-6 text-center p-4 text-muted-text">No assets found</div>}
          >
            <For each={table.getRowModel().rows}>
              {(row) => {
                const isHighlighted = () => props.assetToHighlight === row.original.id;
                let ref!: HTMLDivElement;
                createEffect(() => {
                  if (isHighlighted()) {
                    ref?.scrollIntoView({ behavior: "smooth" });
                    setTimeout(() => {
                      props.setAssetToHighlight("");
                    }, 3000);
                  }
                });
                return (
                  <For each={row.getVisibleCells()}>
                    {(cell, col) => (
                      <div
                        ref={col() === 0 ? ref : undefined}
                        classList={{
                          "sticky left-0 bg-gradient-to-r from-panel via-panel to-transparent": col() === 0,
                          "sticky right-0 bg-gradient-to-l from-panel via-panel to-transparent":
                            col() === columns.length - 1,
                        }}
                        class={twMerge(
                          "px-3 py-2 border-b border-muted-border text-secondary-text",
                          isHighlighted() && "bg-yellow-500/20",
                        )}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </div>
                    )}
                  </For>
                );
              }}
            </For>
          </Show>
        </div>
      </div>
    </div>
  );
};

// Format file size to human readable string
function formatFileSize(bytes: bigint | number) {
  let size = typeof bytes === "bigint" ? Number(bytes) : typeof bytes === "number" ? bytes : 0;
  const units = ["B", "KB", "MB", "GB", "TB"];
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${size.toFixed(1)} ${units[unitIndex]}`;
}
